import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FaDuotoneIconComponent } from '@fortawesome/angular-fontawesome';
import { FinancialService } from '../../../../core/services/financial/financial.service';
import { UserSessionService } from '../../../../core/services/user-session.service';
import { TrialBalanceReportModel, TrialBalanceRowModel } from '../../../../core/models/data/trial-balance-report.model';
import { ChartOfAccountCategory } from '../../../../core/enums/data.enums';
import { CurrencyUtilities } from '../../../../core/utilities/currency.utilities';
import { DateUtilities } from '../../../../core/utilities/date.utilities';
import { ReportDateFilterComponent } from '../../components/report-date-filter/report-date-filter.component';
import { GenericMessagingConstants } from '../../../../core/constants/messaging.constants';

interface TrialBalanceCategoryGroup {
	label: string;
	icon: string;
	accent: 'success' | 'danger' | 'primary' | 'info' | 'warning';
	rows: TrialBalanceRowModel[];
}

@Component({
	selector: 'ms-trial-balance',
	standalone: true,
	imports: [CommonModule, FaDuotoneIconComponent, ReportDateFilterComponent],
	templateUrl: './trial-balance.page.html',
	styleUrl: './trial-balance.page.scss'
})
export class TrialBalancePage implements OnInit {

	private financialService = inject(FinancialService);
	private userSessionService = inject(UserSessionService);

	public readonly asOfDate = signal<string>(DateUtilities.todayISO());
	public readonly report = signal<TrialBalanceReportModel | undefined>(undefined);
	public readonly isLoading = signal<boolean>(false);
	public readonly errorMessage = signal<string>('');

	public readonly groupedRows = computed<TrialBalanceCategoryGroup[]>(() => {
		const current = this.report();
		if (!current?.rows?.length) {
			return [];
		}
		return this.groupRowsByCategory(current.rows);
	});

	public readonly isBalanced = computed<boolean>(() => {
		const current = this.report();
		if (!current) return true;
		return current.totalDebits === current.totalCredits;
	});

	public async ngOnInit(): Promise<void> {
		await this.loadReport();
	}

	public async onDateChanged(): Promise<void> {
		if (!this.asOfDate()) return;
		await this.loadReport();
	}

	public formatAmount(amount: number): string {
		const current = this.report();
		if (!current) return '';
		return CurrencyUtilities.convertMinorUnitToDisplayFormat(amount, current.currency);
	}

	private async loadReport(): Promise<void> {
		const organization = this.userSessionService.activeOrganization();
		if (!organization) {
			this.report.set(undefined);
			this.errorMessage.set('An active organization is required to view this report.');
			return;
		}

		this.isLoading.set(true);
		this.errorMessage.set('');

		const result = await this.financialService.getTrialBalance(organization.organizationId, this.asOfDate());

		if (result.success && result.data) {
			this.report.set(result.data);
		} else {
			this.report.set(undefined);
			this.errorMessage.set(result.message || GenericMessagingConstants.GENERIC_ERROR_OCCURRED());
		}

		this.isLoading.set(false);
	}

	private groupRowsByCategory(rows: TrialBalanceRowModel[]): TrialBalanceCategoryGroup[] {
		const order: (Omit<TrialBalanceCategoryGroup, 'rows'> & { categories: ChartOfAccountCategory[] })[] = [
			{ label: 'Assets', icon: 'wallet', accent: 'success', categories: [ChartOfAccountCategory.AssetsCash, ChartOfAccountCategory.Assets] },
			{ label: 'Liabilities', icon: 'file-invoice-dollar', accent: 'danger', categories: [ChartOfAccountCategory.Liabilities] },
			{ label: 'Equity', icon: 'piggy-bank', accent: 'primary', categories: [ChartOfAccountCategory.Equity] },
			{ label: 'Income', icon: 'arrow-trend-up', accent: 'info', categories: [ChartOfAccountCategory.Revenue] },
			{ label: 'Expenses', icon: 'arrow-trend-down', accent: 'danger', categories: [ChartOfAccountCategory.Expenses] }
		];

		const groups: TrialBalanceCategoryGroup[] = [];
		for (const group of order) {
			const groupRows = rows
				.filter(r => group.categories.includes(r.account.category))
				.sort((a, b) => a.account.accountNumber - b.account.accountNumber);
			if (groupRows.length > 0) {
				groups.push({ label: group.label, icon: group.icon, accent: group.accent, rows: groupRows });
			}
		}
		return groups;
	}

}
